import { type ISODate, daysBetween, daysSince } from './dates';
import { PALETTE, type Task, type TaskStatus, type WorkspaceSettings, avatarColor, statusName } from './types';

/** "Colorir por": agrupa os cartões por uma dimensão e dá cor a cada grupo. */
export const COLOR_MODES = [
  ['none', 'Nada'], ['urgency', 'Urgência'], ['theme', 'Tema'], ['assignee', 'Responsável'],
  ['project', 'Projeto'], ['category', 'Categoria'], ['status', 'Etapa'],
] as const;
export type ColorMode = (typeof COLOR_MODES)[number][0];

export const URGENCY = {
  late: ['Atrasada', '#D0312D', 9],
  today: ['Vence hoje', '#EE6F2D', 8],
  soon: ['Próximos 3 dias', '#F2A372', 5],
  stalled: ['Parada ou a cobrar', '#9A6A0A', 0],
  ok: ['Em dia', '#555555', 0],
  nodue: ['Sem prazo', '#BDBDBD', 0],
} as const;
export const STATUS_COLORS: Record<TaskStatus, string> = { mapped: '#A3A3A3', todo: '#555555', doing: '#EE6F2D', waiting: '#C29A1E', done: '#2E8B57' };
export const NONE_KEY = '_none';

export function colorKeys(t: Task, mode: ColorMode, settings: WorkspaceSettings, today: ISODate): string[] {
  if (mode === 'urgency') {
    if (t.status === 'done') return ['ok'];
    if (t.due && t.due < today) return ['late'];
    if (t.due === today) return ['today'];
    if (t.due && daysBetween(today, t.due) <= 3) return ['soon'];
    if ((t.status === 'doing' && daysSince(t.enteredStatusAt) >= 5) || (t.status === 'waiting' && t.waiting?.followUpOn && t.waiting.followUpOn <= today)) return ['stalled'];
    return [t.due ? 'ok' : 'nodue'];
  }
  if (mode === 'theme') { const ts = t.themeIds.filter(id => settings.themes.some(x => x.id === id)); return ts.length ? ts : [NONE_KEY]; }
  if (mode === 'assignee') return [t.assigneeId || NONE_KEY];
  if (mode === 'project') return [t.projectId && settings.projects.some(p => p.id === t.projectId) ? t.projectId : NONE_KEY];
  if (mode === 'category') { const c = t.classification?.categoryId; return [c && settings.categories.some(x => x.id === c) ? c : NONE_KEY]; }
  if (mode === 'status') return [t.status];
  return [];
}

export interface ColorInfo { name: string; color: string; tint?: number; empty?: boolean }

export function colorInfo(key: string, mode: ColorMode, settings: WorkspaceSettings, person: (id: string) => { name: string; color: string | null }): ColorInfo | null {
  if (key === NONE_KEY) {
    const names: Partial<Record<ColorMode, string>> = { theme: 'Sem tema', assignee: 'Sem responsável', project: 'Sem projeto', category: 'Não classificada' };
    return { name: names[mode] || 'Sem valor', color: 'transparent', empty: true };
  }
  if (mode === 'urgency') { const u = URGENCY[key as keyof typeof URGENCY]; return u ? { name: u[0], color: u[1], tint: u[2] } : null; }
  if (mode === 'theme') { const t = settings.themes.find(x => x.id === key); return { name: t?.name || 'Tema removido', color: t?.color || '#999' }; }
  if (mode === 'assignee') { const p = person(key); return { name: p.name, color: avatarColor(key, p.color) }; }
  if (mode === 'project') { const p = settings.projects.find(x => x.id === key); return { name: p?.name || 'Projeto', color: p?.color || '#999' }; }
  if (mode === 'category') { const i = settings.categories.findIndex(x => x.id === key); return { name: settings.categories[i]?.name || 'Categoria', color: PALETTE[Math.max(0, i) % PALETTE.length] }; }
  if (mode === 'status') return { name: statusName(key as TaskStatus), color: STATUS_COLORS[key as TaskStatus] || '#999' };
  return null;
}
